import React, { ComponentPropsWithRef, useEffect } from 'react';

import Icon, {
    AppstoreAddOutlined,
    BarChartOutlined,
    BookOutlined,
    CalendarOutlined,
    CloudSyncOutlined,
    ClusterOutlined,
    LockOutlined,
    MessageOutlined,
    NotificationOutlined,
    PlusSquareOutlined,
    SolutionOutlined,
    TeamOutlined,
    UsergroupAddOutlined,
} from '@ant-design/icons';
import { faSchool } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReactComponent as Mexcbt } from 'assets/icon/ic_mexcbt.svg';

import { useFeatures } from 'constants/CustomHooks/useFeatures';
import { checkRoleIsSchoolAdmin, generateLtiLaunchDomain } from 'constants/GlobalConfig';
import { useAppSelector } from 'flex/utils';


/**
 * メニューやホームに表示するメニュー情報を取得するカスタムフック
 *
 * @param isFullPath フルパスを取得するかどうか
 */
export const useMenuInfo = (isFullPath = false) => {
    const features = useFeatures();
    const isControllableUser = useAppSelector(state => state.isControllableUser.payload);
    const user = useAppSelector(state => state.user);
    const [items, setItems] = React.useState<MenuInfo[]>([]);

    useEffect(() => {
        setItems(
            MenuInfoList.filter(menuInfo => features[menuInfo.key]).map(menuInfo => {
                if (menuInfo.key === 'deviceList') {
                    return {
                        ...menuInfo,
                        url: generateLtiLaunchToolLink('device-list', isControllableUser, user, isFullPath),
                    };
                }
                if (menuInfo.key === 'dataSync') {
                    return {
                        ...menuInfo,
                        url: generateLtiLaunchToolLink('data-sync', isControllableUser, user, isFullPath),
                    };
                }
                return menuInfo;
            }
            )
        );
    }, [features, isControllableUser, user]);

    return { isLoading: features.isLoading, items };
};

const generateLtiLaunchToolLink = (
    toolPath: 'device-list' | 'data-sync',
    isControllableUser: boolean,
    user: UserData,
    isFullPath: boolean
) => {
    const belong = isControllableUser ?
        user.payload.belongs.filter(byRole('1', 'administrator')) :
        checkRoleIsSchoolAdmin(isControllableUser, user) ?
            user.payload.belongs.filter(byRole('2', 'administrator')) :
            user.payload.belongs.filter(byRole('2', 'teacher'));

    const belongUuid = belong[0]?.uuid ?? '';

    return `${generateLtiLaunchDomain(isControllableUser, isFullPath)}/${toolPath}?belong_uuid=${belongUuid}`;
};

const byRole = (organizationType: string, permissionCode: Permission['code']) => {
    return ({ role }: UserBelong) => role.organization.type === organizationType && role.permission.code === permissionCode;
};

const MexcbtIcon = (props: ComponentPropsWithRef<typeof Icon>) => (
    <Icon {...props} component={Mexcbt} />
);
const OrganizationIcon = (props: ComponentPropsWithRef<typeof Icon>) => (
    <Icon {...props} component={() => <FontAwesomeIcon icon={faSchool as IconProp} />} />
);

type MenuInfo = {
    description: string
    iconOfMenu: JSX.Element
    iconOfTile: JSX.Element
    isOpenNewTab?: boolean
    // Menu の表示非表示を管理するための key
    key: keyof ReturnType<typeof useFeatures>
    subTitle: string
    title: string
    url: string
}

/**
 * メニュー情報の一覧
 *
 * 一部 URL にはダミーの値が入っているので、使用時には注意。
 */
export const MenuInfoList: MenuInfo[] = [
    {
        description: 'MEXCBTテストを配信します.',
        iconOfMenu: <MexcbtIcon />,
        iconOfTile: <MexcbtIcon />,
        key: 'mexcbt',
        subTitle: 'MEXCBTテスト配信の管理・編集・削除',
        title: 'MEXCBTテスト管理',
        url: '/assessment',
    },
    {
        description: 'お知らせ情報を配信します.',
        iconOfMenu: <NotificationOutlined />,
        iconOfTile: <NotificationOutlined />,
        key: 'information',
        subTitle: 'お知らせ情報の管理・編集・削除',
        title: 'お知らせ管理',
        url: '/notifications',
    },
    {
        description: '教材・アプリ管理・アプリグループ構成を設定します.',
        iconOfMenu: <PlusSquareOutlined />,
        iconOfTile: <AppstoreAddOutlined />,
        key: 'application',
        subTitle: 'アプリ情報の管理・編集・削除',
        title: '教材・アプリ管理',
        url: '/apps',
    },
    {
        description: '連携しているアプリケーションのテンプレートの管理・編集・削除を設定します.',
        iconOfMenu: <BookOutlined />,
        iconOfTile: <BookOutlined />,
        key: 'applicationTemplate',
        subTitle: '連携しているアプリケーションのテンプレートの管理・編集・削除を設定',
        title: 'アプリ配信管理',
        url: '/app-templates',
    },
    {
        description: 'アンケート・アンケートカテゴリー構成を設定します.',
        iconOfMenu: <SolutionOutlined />,
        iconOfTile: <SolutionOutlined />,
        key: 'questionnaire',
        subTitle: 'アンケート情報の管理・編集・削除',
        title: 'アンケート',
        url: '/questionnaires',
    },
    {
        description: '利用者情報の管理やクラスと役割を設定します.',
        iconOfMenu: <TeamOutlined />,
        iconOfTile: <UsergroupAddOutlined />,
        key: 'users',
        subTitle: '利用者情報の管理・編集・削除',
        title: '利用者管理',
        url: '/users',
    },
    {
        description: 'テナント内の学校を管理します.',
        iconOfMenu: <OrganizationIcon />,
        iconOfTile: <OrganizationIcon />,
        key: 'organization',
        subTitle: '学校情報の管理・編集・削除',
        title: '学校管理',
        url: '/organization',
    },
    {
        description: '利用履歴を取得します.',
        iconOfMenu: <BarChartOutlined />,
        iconOfTile: <BarChartOutlined />,
        key: 'actionLog',
        subTitle: '',
        title: '利用履歴の取得',
        url: '/usage',
    },
    {
        description: '毎日の記録の配信設定・回答結果を確認できます.',
        iconOfMenu: <CalendarOutlined />,
        iconOfTile: <CalendarOutlined />,
        key: 'everydayNote',
        subTitle: '毎日の記録の管理・編集・削除',
        title: '毎日の記録',
        url: '/everyday-note',
    },
    {
        description: '端末の利用状況を確認します.',
        iconOfMenu: <ClusterOutlined />,
        iconOfTile: <ClusterOutlined />,
        isOpenNewTab: true,
        key: 'deviceList',
        subTitle: '',
        title: '端末管理台帳',
        // URL はユーザーによって変わるため、ここではダミーの URL を設定
        url: '/deviceList',
    },
    {
        description: 'データ連携サービスを起動します.',
        iconOfMenu: <CloudSyncOutlined />,
        iconOfTile: <CloudSyncOutlined />,
        isOpenNewTab: true,
        key: 'dataSync',
        subTitle: '',
        title: 'データ連携サービス',
        // URL はユーザーによって変わるため、ここではダミーの URL を設定
        url: '/dataSync',
    },
    {
        description: 'セキュリティの設定ができます.',
        iconOfMenu: <LockOutlined />,
        iconOfTile: <LockOutlined />,
        key: 'security',
        subTitle: 'セキュリティ設定状況',
        title: 'セキュリティ設定',
        url: '/security',
    },
    {
        description: 'LINEスクール 連絡帳で保護者への連絡やコミュニケーションが行えます。',
        iconOfMenu: <MessageOutlined />,
        iconOfTile: <MessageOutlined />,
        key: 'lineSchool',
        subTitle: '',
        title: '保護者連絡ツール​',
        url: 'https://www.tsunaren.net/',
        isOpenNewTab: true,
    },
];